import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Box } from '@chakra-ui/react';
import LoadingIcon from 'studio_react/common/components/LoadingIcon';
import { ReactComponent as PlayIcon } from 'studio_react/svg/common/play.svg';
import 'video.js/dist/video-js.css';
import { useTranslate } from 'ts/common/hooks';
import VideoControls from './VideoControls';
import VideoInfo from './VideoInfo';
import useVideoPlayer from './useVideoPlayer';

export interface VideoPlayerHandle {
    getCurrentTime: () => number;
    play: () => void;
    pause: () => void;
    stop: () => void;
    seek: (time: number) => void;
    setVolume: (value: number) => void;
}

export interface VideoPlayerState {
    hasStarted: boolean;
    isEnded: boolean;
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    volume: number;
}

interface Props {
    url: string;
    /** MIME type of `url`; defaults to HLS. Pass video/mp4 for a progressive preview clip. */
    mimeType?: string;
    thumbnailUrl?: string;
    fileName?: string;
    fileSize?: string;
    onClose?: () => void;
    onPlayStateChange?: (state: VideoPlayerState) => void;
    /** Suppresses all built-in click-to-play/overlay/controls chrome — for
     * callers (e.g. a custom timeline) that fully drive playback externally. */
    hideOverlay?: boolean;
    spEventBus?: SpAngularJs.ISpEventBus;
    fill?: boolean;
}

const VideoPlayer = forwardRef<VideoPlayerHandle, Props>(function VideoPlayer(
    {
        url,
        mimeType,
        thumbnailUrl,
        fileName,
        fileSize,
        onClose,
        onPlayStateChange,
        hideOverlay = false,
        spEventBus,
        fill = false
    },
    ref
) {
    const {
        containerRef,
        videoRef,
        hasStarted,
        isEnded,
        isPlaying,
        currentTime,
        duration,
        volume,
        isMuted,
        isFullscreen,
        showSettings,
        playbackSpeed,
        quality,
        qualities,
        handleFocus,
        handleBlur,
        handlePlayPause,
        handleSeek,
        handleToggleMute,
        handleVolumeChange,
        handleToggleFullscreen,
        handleSpeedChange,
        handleQualityChange,
        handleReplay,
        toggleSettings,
        getCurrentTime,
        play,
        pause,
        stop
    } = useVideoPlayer({ url, mimeType, thumbnailUrl, onClose, spEventBus, fill });
    const t = useTranslate('studio.common.videoPlayer');
    const [isHovered, setIsHovered] = useState(false);
    const [isThumbnailLoaded, setIsThumbnailLoaded] = useState(!thumbnailUrl);

    useEffect(() => {
        if (!thumbnailUrl) {
            setIsThumbnailLoaded(true);
            return;
        }

        setIsThumbnailLoaded(false);
        let cancelled = false;

        const img = new Image();

        const done = () => {
            if (!cancelled) {
                setIsThumbnailLoaded(true);
            }
        };

        img.onload = done;
        img.onerror = done;
        img.src = thumbnailUrl;

        return () => {
            cancelled = true;
        };
    }, [thumbnailUrl]);

    useImperativeHandle(
        ref,
        () => ({
            getCurrentTime,
            play,
            pause,
            stop,
            seek: handleSeek,
            setVolume: handleVolumeChange
        }),
        [getCurrentTime, play, pause, stop, handleSeek, handleVolumeChange]
    );

    useEffect(() => {
        onPlayStateChange?.({ hasStarted, isEnded, isPlaying, currentTime, duration, volume });
    }, [hasStarted, isEnded, isPlaying, currentTime, duration, volume, onPlayStateChange]);

    return (
        <>
            <Box
                ref={containerRef}
                position="relative"
                w="100%"
                h={fill ? '100%' : undefined}
                display={fill ? 'flex' : undefined}
                alignItems={fill ? 'center' : undefined}
                justifyContent={fill ? 'center' : undefined}
                tabIndex={0}
                outline="none"
                onFocus={handleFocus}
                onBlur={handleBlur}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                sx={{
                    '& .vjs-tech': { objectFit: 'contain' },
                    ...(isFullscreen && {
                        height: '100vh',
                        bg: 'black',
                        '& .video-js': {
                            height: '100vh !important',
                            paddingTop: '0 !important'
                        }
                    })
                }}
            >
                <Box ref={videoRef} w={fill ? '100%' : undefined} h={fill ? '100%' : undefined} />

                {/* Transparent click-to-play overlay — above video.js element, below controls */}
                {!hideOverlay && !isEnded && (
                    <Box
                        position="absolute"
                        top={0}
                        right={0}
                        bottom={0}
                        left={0}
                        zIndex={1}
                        cursor="pointer"
                        onClick={handlePlayPause}
                    />
                )}

                {!hideOverlay && !hasStarted && !isEnded && (
                    <Box
                        position="absolute"
                        top={0}
                        right={0}
                        bottom={0}
                        left={0}
                        zIndex={2}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        pointerEvents="none"
                        backgroundImage={
                            thumbnailUrl && isThumbnailLoaded ? `url(${thumbnailUrl})` : undefined
                        }
                        backgroundSize="contain"
                        backgroundRepeat="no-repeat"
                        backgroundPosition="center"
                        // Opaque fallback so a letterboxed thumbnail doesn't show the video
                        // through it; `backgroundColor` so it doesn't reset backgroundImage.
                        backgroundColor="black"
                    >
                        {thumbnailUrl && !isThumbnailLoaded ? (
                            <LoadingIcon width="64px" height="64px" />
                        ) : (
                            <Box
                                w="100px"
                                h="100px"
                                borderRadius="full"
                                bg="white"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                boxShadow="0px 10px 32px 0px rgba(0,0,0,0.14)"
                                // Literal, not a theme token — "primary.400" only exists in
                                // Studio's theme, not the client gallery's.
                                color="#0C547D"
                                pl="12px"
                            >
                                <PlayIcon width={64} height={64} />
                            </Box>
                        )}
                    </Box>
                )}

                {/* Custom controls overlay — visible on hover or when paused, after first play */}
                {!hideOverlay && hasStarted && !isEnded && (isHovered || !isPlaying) && (
                    <VideoControls
                        isPlaying={isPlaying}
                        currentTime={currentTime}
                        duration={duration}
                        volume={volume}
                        isMuted={isMuted}
                        isFullscreen={isFullscreen}
                        showSettings={showSettings}
                        playbackSpeed={playbackSpeed}
                        quality={quality}
                        qualities={qualities}
                        onPlayPause={handlePlayPause}
                        onSeek={handleSeek}
                        onToggleMute={handleToggleMute}
                        onVolumeChange={handleVolumeChange}
                        onToggleFullscreen={handleToggleFullscreen}
                        onToggleSettings={toggleSettings}
                        onSpeedChange={handleSpeedChange}
                        onQualityChange={handleQualityChange}
                    />
                )}

                {/* Ended overlay — thumbnail + replay button */}
                {!hideOverlay && isEnded && (
                    <Box
                        position="absolute"
                        top={0}
                        right={0}
                        bottom={0}
                        left={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        backgroundImage={
                            thumbnailUrl && isThumbnailLoaded ? `url(${thumbnailUrl})` : undefined
                        }
                        backgroundSize="contain"
                        backgroundRepeat="no-repeat"
                        backgroundPosition="center"
                        // Opaque fallback so a letterboxed thumbnail doesn't show the video
                        // through it; `backgroundColor` so it doesn't reset backgroundImage.
                        backgroundColor="black"
                    >
                        {thumbnailUrl && !isThumbnailLoaded ? (
                            <LoadingIcon width="64px" height="64px" />
                        ) : (
                            <Box
                                as="button"
                                onClick={handleReplay}
                                aria-label={t('replay')}
                                bg="blackAlpha.700"
                                border="2px solid"
                                borderColor="whiteAlpha.800"
                                borderRadius="full"
                                w={16}
                                h={16}
                                cursor="pointer"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                color="white"
                                fontSize="2xl"
                                lineHeight={1}
                            >
                                {/* Rotated on its own span so the glyph's inherent off-center ink isn't dragged along with it. */}
                                <Box as="span" display="inline-flex" transform="rotate(-90deg)">
                                    ↺
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* Filename + filesize — below video, hidden in fullscreen */}
            {!isFullscreen && <VideoInfo fileName={fileName} fileSize={fileSize} />}
        </>
    );
});

export default VideoPlayer;
